import { Box, Divider, Stack } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import { useMediaQueryBreakpoint } from "../../../../../hooks/useMediaQuery";
import EntityPhoto from "../../../../../store/models/entityPhoto";
import { Text } from "../../../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../../../core-ui/components/Text/TextUtils";
import { TrackNavigationButtons } from "../../../ProjectWorkflowPanel/components/TrackNavigationButtons";
import { ReleaseListItemCoverPhoto } from "../../../ReleaseList/ReleaseListItemCoverPhoto";

interface EditReleaseTrackHeaderProps {
  artistName: string | undefined;
  publishDate: string | undefined;
  publishEndDate: string | undefined;
  releaseCover: EntityPhoto | null;
  releaseTitle: string | undefined;
  releaseTrackTitle: string | undefined;
  trackIndex: number;
}

export const EditReleaseTrackHeader = ({
  artistName = "Anonymous",
  publishDate,
  publishEndDate,
  releaseCover,
  releaseTitle = "Untitled",
  releaseTrackTitle = "Untitled",
  trackIndex,
}: EditReleaseTrackHeaderProps) => {
  // Format date to look like "Jan 1st, 2025"
  const formattedPublishDate = useMemo(() => {
    if (!publishDate) return "";
    return format(new Date(publishDate), "MMM do, yyyy");
  }, [publishDate]);

  const formattedPublishEndDate = useMemo(() => {
    if (!publishEndDate) return "";
    return format(new Date(publishEndDate), "MMM do, yyyy");
  }, [publishEndDate]);

  const { isMobile } = useMediaQueryBreakpoint();

  return (
    <Stack gap={3}>
      <Stack direction="row" alignItems="center" gap={2}>
        <ReleaseListItemCoverPhoto
          releaseCover={releaseCover}
          hasShadow={false}
        />
        <Stack>
          <Text bold variant={TextStyleVariant.P2}>
            {releaseTitle}
          </Text>
          <Text variant={TextStyleVariant.P3}>{artistName}</Text>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text color={TextColor.SECONDARY} variant={TextStyleVariant.P4}>
              {formattedPublishDate}
            </Text>
            {formattedPublishEndDate && (
              <Text color={TextColor.SECONDARY} variant={TextStyleVariant.P4}>
                - {formattedPublishEndDate}
              </Text>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
        >
          <Stack direction="row" alignItems="flex-start" gap={0.5}>
            <Text bold variant={TextStyleVariant.P2} sx={{ minWidth: "70px" }}>
              Track title:
            </Text>
            <Text
              variant={TextStyleVariant.P2}
              sx={{ wordBreak: "break-word" }}
            >
              {releaseTrackTitle}
            </Text>
          </Stack>
          {/* TODO: Add track navigation logic once ReleaseTrack Table is implemented */}
          <TrackNavigationButtons
            currentIndex={trackIndex - 1} // trackIndex is 1-indexed
            totalNumberOfTracks={trackIndex}
            handleSelectTrack={() => {}}
            isMobile={isMobile}
          />
        </Stack>
        <Divider sx={{ my: 1.5 }} />
      </Box>
    </Stack>
  );
};
